import { useState, createContext } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  isRouteErrorResponse,
  useRouteError,
} from "@remix-run/react";
import type { LinksFunction } from "@remix-run/node";
import Header from "./ui-apps/components/Header";
import Footer from "./ui-apps/components/Footer";
import styles from "./tailwind.css?url";
import SignedOutSideNavbar from "./ui-apps/components/SignedOutSideNavbar";
import SignedInSideNavbar from "./ui-apps/components/SignedInSideNavbar";
import DesktopHeaderSearch from "./ui-apps/components/DesktopHeaderSearch";
import { UserContext } from "./utilities/userContext";
import { securedRouteLoader } from "./utilities/secured-route.server";

export { securedRouteLoader as loader };

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, displayPricingModal, isBeta } = useLoaderData<typeof securedRouteLoader>();
  return (
    <html lang="en" className="h-full bg-white">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="bg-white min-h-full text-black">
        <UserContext.Provider value={{ isAuthenticated, user }}>
          <div className="flex min-h-screen">
            {
              isAuthenticated ? (
                <SignedInSideNavbar displayPricingModal={displayPricingModal} user={user} />
              ) : (
                <SignedOutSideNavbar isAuthenticated={isAuthenticated} />
              )
            }

            <div className={`flex-1 flex flex-col lg:pl-64`}>
              <DesktopHeaderSearch />

              <Header
                setMobileMenuOpen={setMobileMenuOpen}
                mobileMenuOpen={mobileMenuOpen}
                isAuthenticated={isAuthenticated}
              />

              <main className="flex-grow">
                <Outlet context={{ auth: { isAuthenticated, user, displayPricingModal, isBeta } }} />
              </main>
              <Footer isAuthenticated={isAuthenticated} />
            </div>
          </div>
        </UserContext.Provider>
        <script src="https://cdn.jsdelivr.net/npm/echarts@5.3.2/dist/echarts.min.js"></script>
        <Scripts />
        <ScrollRestoration />
      </body>
    </html>
  );
}

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: styles },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <html lang="en" className="h-full">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>{`${error.status} ${error.statusText}`}</title>
          <Links />
        </head>
        <body className="md:bg-white min-h-full">
          <div className="flex min-h-screen items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {error.status} {error.statusText}
              </h1>
              <p className="text-gray-600 mb-8">
                {error.data?.message || "Something went wrong"}
              </p>
              <a
                href="/"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800"
              >
                Go back home
              </a>
            </div>
          </div>
          <Scripts />
        </body>
      </html>
    );
  }

  return (
    <html lang="en" className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Unexpected Error</title>
        <Links />
      </head>
      <body className="md:bg-white min-h-full">
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Unexpected Error
            </h1>
            <p className="text-gray-600 mb-8">
              An unexpected error occurred. Please try again later.
            </p>
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800"
            >
              Go back home
            </a>
          </div>
        </div>
        <Scripts />
      </body>
    </html>
  );
}

function Document() {
  return <App />;
}

export default Document;